// --- Dependencies -------------------------------------------------------------------------------
import { Checkbox, FormControl, InputLabel, MenuItem, Select, type SelectChangeEvent } from "@mui/material";
import { useId } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import "../field.css";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = {
    value: string[];
    onChange: (value: string[]) => void;
    options: { value: string; label: string }[];
    label?: string;
    placeholder?: string;
    disabled?: boolean;
    /** The short, bar-height variant. The tall one belongs in a side form. */
    compact?: boolean;
};

/**
 * MUI's select with `multiple`, wearing our tokens — the same treatment `SelectInput` gets, because a
 * native multiple select cannot be styled to match anything.
 *
 * Checkboxes in the menu rather than highlighted rows: a person has to see what is *not* chosen as
 * clearly as what is, or a filter with two of four options looks like a filter with two options.
 */
export default function MultiSelectInput({ value, onChange, options, label, placeholder, disabled, compact }: Props) {
    const id = useId();

    const handleChange = (event: SelectChangeEvent<string[]>) => {
        const next = event.target.value;
        onChange(typeof next === "string" ? next.split(",") : next);
    };

    return (
        <FormControl
            className={`field multi-select${compact ? " field--compact multi-select--compact" : ""}`}
            fullWidth={!compact}
            disabled={disabled}
        >
            {label && (
                <InputLabel id={id} shrink={!!value.length}>
                    {label}
                </InputLabel>
            )}

            <Select
                labelId={label ? id : undefined}
                label={label}
                notched={label ? !!value.length : undefined}
                multiple
                value={value}
                onChange={handleChange}
                displayEmpty
                renderValue={(selected) => {
                    if (!selected.length) return <span className="multi-select__placeholder">{placeholder}</span>;
                    return options
                        .filter((option) => selected.includes(option.value))
                        .map((option) => option.label)
                        .join("، ");
                }}
                MenuProps={{ slotProps: { paper: { className: "multi-select__paper" } } }}
            >
                {options.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                        <Checkbox className="multi-select__check" checked={value.includes(option.value)} size="small" disableRipple />
                        {option.label}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}
